import React, { useState, useEffect } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Stack } from '@mui/material';
import { AspectRatio, Box, Card, CardContent, CardOverflow, Chip, Typography, Tooltip } from '@mui/joy';
import FavoriteIcon from '@mui/icons-material/Favorite'; import InterestChips from '../custom/InterestChips.jsx';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChartComponent from '../custom/CustomChart.jsx';
import {
    calculateOffset,
    getSortedMinCo2Modes,
    getFirebaseImage,
} from '../../helpers/Functions.js';
import { calculateOverallScore } from '../../helpers/SF.js';
import { Link } from 'react-router-dom';
import { red } from '@mui/material/colors';
import CityCardTable from '../city/CityCardTable'

const CityCardItem = ({ index, fromDestination, toDestination, month, minCo2Mode, averages, sortBy, clicked, onItemClicked, interests }) => {
    const [image, setImage] = useState();

    // uncomment
    useEffect(() => {
        let wasUnmounted = false;
        getFirebaseImage(`${toDestination.id}.jpg`).then((image) => {
            if (wasUnmounted) return;
            setImage(image);
        });
        return () => {
            wasUnmounted = true;
        };
    }, [toDestination.id]);

    const offset = calculateOffset(
        'emission',
        averages,
        minCo2Mode.co2,
        toDestination.popularity.popularity_score,
        toDestination.seasonality[month]
    );

    const generateHighlight = (sortBy) => {
        let label = '';
        if (sortBy === 'overall') {
            label = 'Green Recommended';
        } else if (sortBy === 'emission') {
            label = 'Lowest Emission';
        } else if (sortBy === 'popularity') {
            label = 'Hidden Gem';
        } else if (sortBy === 'seasonality') {
            label = 'Least Crowded';
        } else if (sortBy === 'interest') {
            label = 'Interest Match';
        } else if (sortBy === 'history') {
            label = 'Related Trip';
        }
        return (
            index < 3 && (
                <Chip
                    variant='solid'
                    color='success'
                    size='sm'
                    sx={{
                        position: 'absolute',
                        top: 8,
                        left: -8,
                        borderRadius: 'sm',
                        py: 0.25,
                        px: 0.5,
                    }}
                >
                    {label}
                </Chip>
            )
        );
    };

    const modesSortedByCo2 = getSortedMinCo2Modes(fromDestination, toDestination);
    const overallScore = calculateOverallScore(fromDestination, toDestination, month);

    return (
        modesSortedByCo2 && (
            <Card
                sx={{
                    // width: 420,
                    minHeight: 500,
                    maxWidth: '100%',
                    boxShadow: 'lg',
                }}
            >
                <CardOverflow>
                    <Box sx={{ position: 'relative' }}>
                        <Link
                            to={`/explore/${toDestination.id}`}
                            key={toDestination.id}
                            style={{ textDecoration: 'none' }}
                            onClick={() => window.scrollTo(0, 0)}
                        >
                            <AspectRatio sx={{
                                minWidth: 200, '&:hover': {
                                    scale: '0.98',
                                    transitionDuration: "500ms",
                                    transitionTimingFunction:"cubic-bezier(0.4, 0, 0.2, 1)",

                                },
                            }}>
                                <Tooltip color='neutral' variant='soft' title='Click to see more'>
                                    <img src={image} loading='lazy' alt='city' />
                                </Tooltip>
                            </AspectRatio>
                        </Link>

                        {generateHighlight(sortBy)}
                        <Tooltip color='neutral' placement='top' variant='soft' title='Overall score'>
                            <Chip
                                variant='solid'
                                size='md'
                                color={
                                    overallScore >= 40
                                        ? 'danger'
                                        : overallScore >= 20
                                            ? 'warning'
                                            : overallScore >= 0
                                                ? 'success'
                                                : 'neutral'
                                }
                                sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: -8,
                                    borderRadius: 'sm',
                                    px: 1,
                                    py: 0.5,
                                }}
                            >
                                {Math.floor(overallScore)}/100
                            </Chip>
                        </Tooltip>
                        <Tooltip sx={{}} placement='top' variant='soft' title={clicked ? 'Added to Trips' : 'Add to Trips'} onClick={onItemClicked}>
                            <FavoriteIcon sx={{
                                color: clicked ? red[500] : '#fff', position: 'absolute',
                                bottom: 8,
                                right: -8,
                                fontSize: 32,
                                cursor: "pointer"
                            }} />
                        </Tooltip>
                    </Box>
                </CardOverflow>
                <CardContent>
                    <Accordion sx={{ boxShadow: 0, "textOverflow": "ellipsis" }}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="panel1-content"
                            id="panel1-header"
                        >
                            <Typography level='title-md' sx={{ "mb": 0, "fontWeight": 'lg', "flex": "1", "overflow": "hidden", "textOverflow": "ellipsis" }}>
                                {toDestination.name}, {toDestination.country}, {toDestination.flag}
                            </Typography>

                        </AccordionSummary>
                        <AccordionDetails sx={{ paddingBottom: '0px' }}>
                            <Typography level='body-xs'>{toDestination.description.split('. ')[0]}.</Typography>
                            <InterestChips interests={toDestination.interests}></InterestChips>
                        </AccordionDetails>
                    </Accordion>

                    <CityCardTable toDestination={toDestination} month={month} minCo2Mode={minCo2Mode} averages={averages} userInterests={interests} cityInterests={toDestination.interests} />

                    <Box sx={{ p: 1, backgroundColor: '#fff', borderRadius: 0 }}>
                        <Stack direction={{ xs: 'row', md: 'row' }} spacing={{ xs: 1, md: 0 }}>
                            {modesSortedByCo2.map((mode, index) => (
                                <ChartComponent
                                    key={index}
                                    mode={mode}
                                    index={index}
                                    sum={offset}
                                    averages={averages.co2}
                                />
                            ))}
                        </Stack>
                    </Box>
                </CardContent>
            </Card>
        )
    );
};

export default CityCardItem;
